import { motion } from 'framer-motion';
import { useEffect, useState, useMemo } from 'react';

// Tesseract cube component - 3D wireframe representation
function TesseractCube({ 
	size = 100, 
	color = 'babyblue', 
	position = { x: 0, y: 0 },
	rotationSpeed = 1,
	delay = 0,
	depth = 0,
	initialRotation = { x: 0, y: 0, z: 0 }
}: { 
	size?: number; 
	color?: 'babyblue' | 'royalpurple' | 'orangepeel'; 
	position?: { x: number | string; y: number | string };
	rotationSpeed?: number;
	delay?: number;
	depth?: number;
	initialRotation?: { x: number; y: number; z: number };
}) {
	const [rotation, setRotation] = useState(initialRotation);
	
	const colorMap = {
		babyblue: '#0099FF',
		royalpurple: '#9900FF',
		orangepeel: '#FF9900'
	};

	useEffect(() => {
		let animationFrameId: number;
		let lastTime = performance.now();
		const targetFPS = 30; // Reduced FPS for slower, more subtle animation
		const frameInterval = 1000 / targetFPS;
		// Significantly reduced rotation speeds for slow, gradual state changes
		const slowRotationMultiplier = 0.15; // Much slower rotation

		const animate = (currentTime: number) => {
			const deltaTime = currentTime - lastTime;
			if (deltaTime >= frameInterval) {
				setRotation(prev => ({
					x: prev.x + rotationSpeed * 0.01 * slowRotationMultiplier * (deltaTime / 33.33), // Very slow X rotation
					y: prev.y + rotationSpeed * 0.015 * slowRotationMultiplier * (deltaTime / 33.33), // Very slow Y rotation
					z: prev.z + rotationSpeed * 0.008 * slowRotationMultiplier * (deltaTime / 33.33) // Very slow Z rotation
				}));
				lastTime = currentTime;
			}
			animationFrameId = requestAnimationFrame(animate);
		};

		animationFrameId = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(animationFrameId);
	}, [rotationSpeed]);

	// Cube vertices (8 corners of a cube)
	const vertices = [
		[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
		[-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]
	];

	// Cube edges (12 edges)
	const edges = [
		[0, 1], [1, 2], [2, 3], [3, 0], // front face
		[4, 5], [5, 6], [6, 7], [7, 4], // back face
		[0, 4], [1, 5], [2, 6], [3, 7]  // connecting edges
	];

	// Rotation matrices
	const rotateX = (x: number, y: number, z: number, angle: number) => {
		const cos = Math.cos(angle);
		const sin = Math.sin(angle);
		return { x, y: y * cos - z * sin, z: y * sin + z * cos };
	};

	const rotateY = (x: number, y: number, z: number, angle: number) => {
		const cos = Math.cos(angle);
		const sin = Math.sin(angle);
		return { x: x * cos + z * sin, y, z: -x * sin + z * cos };
	};

	const rotateZ = (x: number, y: number, z: number, angle: number) => {
		const cos = Math.cos(angle);
		const sin = Math.sin(angle);
		return { x: x * cos - y * sin, y: x * sin + y * cos, z };
	};

	// Project 3D to 2D
	const project = (x: number, y: number, z: number) => {
		const distance = 5;
		const scale = distance / (distance + z);
		return { x: x * scale * size, y: y * scale * size };
	};

	// Rotate and project vertices
	const rotatedVertices = vertices.map(([x, y, z]) => {
		let point = { x, y, z };
		point = rotateX(point.x, point.y, point.z, rotation.x);
		point = rotateY(point.x, point.y, point.z, rotation.y);
		point = rotateZ(point.x, point.y, point.z, rotation.z);
		const projected = project(point.x, point.y, point.z);
		return { ...projected, z: point.z };
	});

	// Calculate opacity and z-index based on depth
	const depthOpacity = Math.max(0.2, Math.min(1, 1 - depth * 0.15));
	const zIndex = Math.round(10 - depth * 2);

	return (
		<motion.div
			className="absolute"
			style={{
				left: typeof position.x === 'string' ? position.x : `calc(50% + ${position.x}px)`,
				top: typeof position.y === 'string' ? position.y : `calc(50% + ${position.y}px)`,
				transform: 'translate3d(-50%, -50%, 0) translateZ(0)',
				zIndex: zIndex,
				opacity: depthOpacity,
				willChange: 'transform, opacity',
			}}
			initial={{ opacity: 0, scale: 0 }}
			animate={{ opacity: depthOpacity, scale: 1 }}
			transition={{ delay, duration: 1 }}
		>
			<svg
				width={size * 2}
				height={size * 2}
				viewBox={`${-size} ${-size} ${size * 2} ${size * 2}`}
				className="overflow-visible"
			>
				{edges.map(([start, end], idx) => {
					const v1 = rotatedVertices[start];
					const v2 = rotatedVertices[end];
					const depth = (v1.z + v2.z) / 2;
					const opacity = Math.max(0.3, Math.min(1, (depth + 2) / 4));
					
					return (
						<line
							key={idx}
							x1={v1.x}
							y1={v1.y}
							x2={v2.x}
							y2={v2.y}
							stroke={colorMap[color]}
							strokeWidth="3"
							opacity={opacity}
							strokeLinecap="round"
							style={{
								filter: `drop-shadow(0 0 8px ${colorMap[color]})`,
								willChange: 'opacity',
							}}
						/>
					);
				})}
				{rotatedVertices.map((v, idx) => {
					const opacity = Math.max(0.7, Math.min(1, (v.z + 2) / 4));
					return (
						<circle
							key={`vertex-${idx}`}
							cx={v.x}
							cy={v.y}
							r="5"
							fill={colorMap[color]}
							opacity={opacity}
							style={{
								filter: `drop-shadow(0 0 10px ${colorMap[color]})`,
								willChange: 'opacity',
							}}
						/>
					);
				})}
			</svg>
		</motion.div>
	);
}

// Neural Network Grid Component - Optimized for performance
function NeuralNetworkGrid() {
	const gridSize = 50; // Aligns with the main grid
	
	// Track viewport dimensions and update on resize
	const [viewportSize, setViewportSize] = useState({
		width: typeof window !== 'undefined' ? window.innerWidth : 1920,
		height: typeof window !== 'undefined' ? window.innerHeight : 1080,
	});
	
	// Track mouse position for interactive grid - throttled for performance
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	
	useEffect(() => {
		let rafId: number;
		let lastMouseUpdate = 0;
		const throttleMs = 16; // ~60fps updates
		
		const handleResize = () => {
			setViewportSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};
		
		const handleMouseMove = (e: MouseEvent) => {
			const now = performance.now();
			if (now - lastMouseUpdate >= throttleMs) {
				setMousePosition({ x: e.clientX, y: e.clientY });
				lastMouseUpdate = now;
			} else {
				// Use RAF for smooth updates
				if (!rafId) {
					rafId = requestAnimationFrame(() => {
						setMousePosition({ x: e.clientX, y: e.clientY });
						rafId = 0;
					});
				}
			}
		};
		
		window.addEventListener('resize', handleResize, { passive: true });
		window.addEventListener('mousemove', handleMouseMove, { passive: true });
		return () => {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('mousemove', handleMouseMove);
			if (rafId) cancelAnimationFrame(rafId);
		};
	}, []);
	
	const nodes = useMemo(() => {
		const viewportWidth = viewportSize.width;
		const viewportHeight = viewportSize.height;
		const nodeSpacing = 6; // Every 6th grid intersection (300px spacing)
		const cols = Math.floor(viewportWidth / (gridSize * nodeSpacing));
		// Calculate visible rows based on viewport height
		const visibleRows = Math.floor(viewportHeight / (gridSize * nodeSpacing));
		// Add extra rows to ensure grid extends beyond viewport
		const rows = Math.ceil(viewportHeight / (gridSize * nodeSpacing)) + 1;
		
		const nodeList: Array<{x: number, y: number, layer: number, color: string}> = [];
		const colors = ['#0099FF', '#9900FF', '#FF9900'];
		
		// Create consistent grid-aligned nodes
		// Divide rows into three clear sections: blue (top), purple (middle), orange (bottom)
		// Use visibleRows for color distribution to keep orange visible
		for (let row = 0; row <= rows; row++) {
			for (let col = 0; col <= cols; col++) {
				const x = col * gridSize * nodeSpacing;
				const y = row * gridSize * nodeSpacing;
				
				// Clear three-section distribution based on visible viewport
				let layer = 0;
				if (visibleRows > 0) {
					// Use visibleRows for color calculation, not total rows
					const rowProgress = row / visibleRows;
					if (rowProgress < 0.33) {
						layer = 0; // Blue - top third
					} else if (rowProgress < 0.67) {
						layer = 1; // Purple - middle third
					} else {
						layer = 2; // Orange - bottom third
					}
				}
				// Any rows beyond visible area also get orange to maintain consistency
				if (row > visibleRows) {
					layer = 2; // Orange for overflow rows
				}
				nodeList.push({ x, y, layer, color: colors[layer] });
			}
		}
		
		return nodeList;
	}, [viewportSize.width, viewportSize.height]);
	
	const connections = useMemo(() => {
		const conns: Array<{
			from: number;
			to: number;
			weight: number;
			pathPoints: Array<{x: number, y: number}>;
		}> = [];
		const squareSize = gridSize * 6; // 300px squares
		
		// Create consistent square loops on the grid
		for (let i = 0; i < nodes.length; i++) {
			const node = nodes[i];
			
			// Connect to adjacent nodes in the grid (right and down only for consistency)
			const nodeSpacing = 6;
			const nodeCol = node.x / (gridSize * nodeSpacing);
			const nodeRow = node.y / (gridSize * nodeSpacing);
			
			// Find adjacent nodes
			for (let j = i + 1; j < nodes.length; j++) {
				const otherNode = nodes[j];
				const otherCol = otherNode.x / (gridSize * nodeSpacing);
				const otherRow = otherNode.y / (gridSize * nodeSpacing);
				
				const colDiff = Math.abs(otherCol - nodeCol);
				const rowDiff = Math.abs(otherRow - nodeRow);
				
				// Only connect adjacent nodes (horizontally or vertically adjacent)
				if ((colDiff === 1 && rowDiff === 0) || (colDiff === 0 && rowDiff === 1)) {
					// Create perfect square loop on grid
					const pathPoints: Array<{x: number, y: number}> = [];
					
					// Start from first node
					pathPoints.push({ x: node.x, y: node.y });
					
					// Create grid-aligned square loop
					if (colDiff === 1) {
						// Horizontal connection - create square extending vertically
						const squareTop = Math.round((Math.min(node.y, otherNode.y) - squareSize / 2) / gridSize) * gridSize;
						const squareBottom = Math.round((Math.max(node.y, otherNode.y) + squareSize / 2) / gridSize) * gridSize;
						const squareLeft = node.x;
						const squareRight = otherNode.x;
						
						// Perfect square path: start -> top-left -> top-right -> bottom-right -> bottom-left -> top-left -> start -> end
						pathPoints.push({ x: squareLeft, y: squareTop });
						pathPoints.push({ x: squareRight, y: squareTop });
						pathPoints.push({ x: squareRight, y: squareBottom });
						pathPoints.push({ x: squareLeft, y: squareBottom });
						pathPoints.push({ x: squareLeft, y: squareTop });
						pathPoints.push({ x: node.x, y: node.y });
					} else {
						// Vertical connection - create square extending horizontally
						const squareLeft = Math.round((Math.min(node.x, otherNode.x) - squareSize / 2) / gridSize) * gridSize;
						const squareRight = Math.round((Math.max(node.x, otherNode.x) + squareSize / 2) / gridSize) * gridSize;
						const squareTop = node.y;
						const squareBottom = otherNode.y;
						
						// Perfect square path
						pathPoints.push({ x: squareLeft, y: squareTop });
						pathPoints.push({ x: squareRight, y: squareTop });
						pathPoints.push({ x: squareRight, y: squareBottom });
						pathPoints.push({ x: squareLeft, y: squareBottom });
						pathPoints.push({ x: squareLeft, y: squareTop });
						pathPoints.push({ x: node.x, y: node.y });
					}
					
					// End at second node
					pathPoints.push({ x: otherNode.x, y: otherNode.y });
					
					conns.push({
						from: i,
						to: j,
						weight: 0.5 + (node.layer + otherNode.layer) * 0.15,
						pathPoints,
					});
				}
			}
		}
		
		return conns;
	}, [nodes]);
	
	
	// Calculate proximity effects for each connection based on mouse position - smooth gradual falloff
	const connectionProximities = useMemo(() => {
		const interactionRadius = 300;
		const falloffPower = 2; // Gentler falloff for smoother transitions
		
		return connections.map((conn) => {
			let minDistance = Infinity;
			
			for (let i = 0; i < conn.pathPoints.length - 1; i++) {
				const p1 = conn.pathPoints[i];
				const p2 = conn.pathPoints[i + 1];
				
				// Calculate distance from mouse to line segment
				const dx = p2.x - p1.x;
				const dy = p2.y - p1.y;
				const length = Math.sqrt(dx * dx + dy * dy);
				
				if (length > 0) {
					const t = Math.max(0, Math.min(1, ((mousePosition.x - p1.x) * dx + (mousePosition.y - p1.y) * dy) / (length * length)));
					const projX = p1.x + t * dx;
					const projY = p1.y + t * dy;
					const dist = Math.sqrt((mousePosition.x - projX) ** 2 + (mousePosition.y - projY) ** 2);
					minDistance = Math.min(minDistance, dist);
				}
			}
			// Smooth gradual falloff - returns to 0 when mouse is far away
			const normalizedDist = Math.min(minDistance / interactionRadius, 1);
			const proximityGlow = Math.pow(Math.max(0, 1 - normalizedDist), falloffPower);
			
			return { 
				glow: proximityGlow, 
				distance: minDistance
			};
		});
	}, [connections, mousePosition.x, mousePosition.y]);
	
	return (
		<svg className="absolute inset-0 pointer-events-none overflow-visible" style={{ zIndex: 0, width: '100%', height: '100vh' }}>
			{/* Square decision loops - consistent grid-aligned squares */}
			{connections.map((conn, idx) => {
				const fromNode = nodes[conn.from];
				const color = fromNode.color;
				const delay = (idx * 0.3) % 2; // Consistent staggered delays
				const proximityData = connectionProximities[idx];
				const proximityGlow = proximityData.glow;
				
				// Create grid-aligned square path from pathPoints
				const pathString = conn.pathPoints.map((point, pointIdx) => 
					pointIdx === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
				).join(' ');
				
				const baseOpacity = 0.1;
				const hoverOpacity = 0.25 + conn.weight * 0.15;
				// Smooth gradual interaction - returns to base when not interacting
				const enhancedOpacity = baseOpacity + (hoverOpacity - baseOpacity) * (1 + proximityGlow * 0.6);
				const baseStrokeWidth = 0.5 + conn.weight * 0.5;
				const enhancedStrokeWidth = baseStrokeWidth + proximityGlow * 2;
				const baseGlow = 2;
				const enhancedGlow = baseGlow + proximityGlow * 12;
				// Subtle pulsing only when very close
				const pulseIntensity = proximityGlow > 0.85 ? Math.sin(Date.now() / 300) * 0.08 + 1 : 1;
				
				return (
					<motion.path
						key={`conn-${idx}`}
						d={pathString}
						stroke={color}
						strokeWidth={enhancedStrokeWidth * pulseIntensity}
						fill="none"
						strokeLinecap="round"
						strokeLinejoin="round"
						initial={{ pathLength: 0, opacity: baseOpacity }}
						animate={{
							pathLength: [0, 1],
							opacity: [
								baseOpacity + proximityGlow * (hoverOpacity - baseOpacity) * 0.3, 
								enhancedOpacity * pulseIntensity, 
								baseOpacity + proximityGlow * (hoverOpacity - baseOpacity) * 0.3
							],
						}}
						transition={{
							duration: 3 + conn.weight,
							repeat: Infinity,
							ease: [0.4, 0, 0.2, 1],
							delay: delay,
							opacity: {
								duration: 0.4,
								ease: [0.25, 0.1, 0.25, 1], // Smoother, more gradual response
							},
							strokeWidth: {
								duration: 0.4,
								ease: [0.25, 0.1, 0.25, 1], // Smooth gradual transition
							},
						}}
						style={{
							willChange: 'pathLength, opacity, strokeWidth',
							filter: `drop-shadow(0 0 ${enhancedGlow * pulseIntensity}px ${color})`,
						}}
					/>
				);
			})}
			
			{/* Neural nodes - pulsing animations with enhanced mouse interaction */}
			{nodes.map((node, idx) => {
				const pulseDelay = ((node.x + node.y) * 0.0005) % 2;
				// Calculate distance from mouse to node for interaction
				const dx = mousePosition.x - node.x;
				const dy = mousePosition.y - node.y;
				const mouseDistance = Math.sqrt(dx * dx + dy * dy);
				const nodeInteractionRadius = 180;
				const nodeProximity = Math.max(0, 1 - mouseDistance / nodeInteractionRadius);
				const nodeProximityGlow = Math.pow(nodeProximity, 2); // Gentler, smoother falloff - returns to 0 when far
				
				// Base values - return to these when not interacting
				const baseGlowRadius = 4;
				const baseCoreRadius = 3;
				const baseGlowOpacity = 0.3;
				const baseCoreOpacity = 0.7;
				const baseGlowSize = 8;
				const baseCoreGlow = 6;
				
				// Enhanced values when mouse is near - gradual transition
				const enhancedGlowRadius = baseGlowRadius + nodeProximityGlow * 3;
				const enhancedCoreRadius = baseCoreRadius + nodeProximityGlow * 2;
				
				// Subtle pulsing only when very close
				const isVeryClose = nodeProximity > 0.8;
				const pulseSpeed = isVeryClose ? 400 : 2000;
				const pulseIntensity = isVeryClose ? Math.sin(Date.now() / pulseSpeed) * 0.1 + 1 : 1;
				
				return (
					<g key={`node-${idx}`}>
						{/* Outer ripple effect when mouse is very close - subtle */}
						{isVeryClose && (
							<motion.circle
								cx={node.x}
								cy={node.y}
								r={enhancedGlowRadius * 1.6}
								fill="none"
								stroke={node.color}
								strokeWidth="0.5"
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{
									opacity: [0.3, 0],
									scale: [0.9, 1.3],
								}}
								transition={{
									duration: 1.2,
									repeat: Infinity,
									ease: [0.4, 0, 0.6, 1],
								}}
								style={{
									filter: `drop-shadow(0 0 6px ${node.color})`,
									willChange: 'opacity, scale',
								}}
							/>
						)}
						{/* Node glow - returns to base when not interacting */}
						<motion.circle
							cx={node.x}
							cy={node.y}
							r={enhancedGlowRadius * pulseIntensity}
							fill={node.color}
							initial={{ opacity: baseGlowOpacity }}
							animate={{
								opacity: [
									baseGlowOpacity + nodeProximityGlow * 0.2, 
									0.6 + nodeProximityGlow * 0.3, 
									baseGlowOpacity + nodeProximityGlow * 0.2
								],
								r: [
									enhancedGlowRadius * pulseIntensity, 
									(enhancedGlowRadius + 1) * pulseIntensity, 
									enhancedGlowRadius * pulseIntensity
								],
							}}
							transition={{
								duration: 2,
								repeat: Infinity,
								ease: [0.4, 0, 0.6, 1],
								delay: pulseDelay,
								r: {
									duration: 0.35,
									ease: [0.25, 0.1, 0.25, 1], // Smooth gradual transition
								},
								opacity: {
									duration: 0.35,
									ease: [0.25, 0.1, 0.25, 1], // Smooth gradual transition
								},
							}}
							style={{
								filter: `drop-shadow(0 0 ${(baseGlowSize + nodeProximityGlow * 14) * pulseIntensity}px ${node.color})`,
								willChange: 'opacity, r',
							}}
						/>
						{/* Node core - returns to base when not interacting */}
						<motion.circle
							cx={node.x}
							cy={node.y}
							r={enhancedCoreRadius * pulseIntensity}
							fill={node.color}
							initial={{ opacity: baseCoreOpacity }}
							animate={{
								opacity: [
									baseCoreOpacity + nodeProximityGlow * 0.2, 
									1, 
									baseCoreOpacity + nodeProximityGlow * 0.2
								],
							}}
							transition={{
								duration: 1.5,
								repeat: Infinity,
								ease: [0.4, 0, 0.6, 1],
								delay: pulseDelay + 0.3,
								opacity: {
									duration: 0.35,
									ease: [0.25, 0.1, 0.25, 1], // Smooth gradual transition
								},
							}}
							style={{
								filter: `drop-shadow(0 0 ${(baseCoreGlow + nodeProximityGlow * 12) * pulseIntensity}px ${node.color})`,
								willChange: 'opacity',
							}}
						/>
					</g>
				);
			})}
		</svg>
	);
}

export default function Home() {

	return (
		<div className="relative h-screen overflow-hidden bg-secondary text-secondary-foreground flex items-center justify-center">
			{/* Animated background gradients */}
			<div className="absolute inset-0 bg-radial-faint" />
			<div className="absolute inset-0 bg-radial-cyan" />
			
			{/* Enhanced gradient overlays */}
			<div className="absolute inset-0 bg-gradient-to-br from-babyblue/6 via-transparent to-royalpurple/6" />
			<div className="absolute inset-0 bg-gradient-to-tl from-orangepeel/5 via-transparent to-babyblue/5" />
			<div className="absolute inset-0 bg-gradient-to-r from-transparent via-royalpurple/3 to-transparent" />
			
			{/* Floating tesseract cubes - positioned with overflow, more visible */}
			<TesseractCube 
				size={350} 
				color="babyblue" 
				position={{ x: '5%', y: '15%' }}
				rotationSpeed={0.5}
				delay={0}
				depth={1}
				initialRotation={{ x: Math.PI / 4, y: Math.PI / 6, z: 0 }}
			/>
			<TesseractCube 
				size={380} 
				color="royalpurple" 
				position={{ x: '75%', y: '10%' }}
				rotationSpeed={0.4}
				delay={0.2}
				depth={1}
				initialRotation={{ x: 0, y: Math.PI / 3, z: Math.PI / 4 }}
			/>
			<TesseractCube 
				size={400} 
				color="orangepeel" 
				position={{ x: '50%', y: '85%' }}
				rotationSpeed={0.45}
				delay={0.4}
				depth={0}
				initialRotation={{ x: Math.PI / 6, y: 0, z: Math.PI / 3 }}
			/>

			{/* Optimized floating orbs for depth */}
			<motion.div
				className="absolute top-6 left-6 h-64 w-64 rounded-full bg-royalpurple/18 blur-2xl"
				animate={{ 
					y: [0, 20, 0], 
					x: [0, 15, 0], 
					scale: [1, 1.05, 1]
				}}
				transition={{ duration: 20, repeat: Infinity, ease: [0.4, 0, 0.6, 1] }}
				style={{ willChange: 'transform', transform: 'translateZ(0)' }}
			/>
			<motion.div
				className="absolute bottom-6 right-6 h-80 w-80 rounded-full bg-babyblue/12 blur-2xl"
				animate={{ 
					y: [0, -20, 0], 
					x: [0, -15, 0], 
					scale: [1, 1.08, 1]
				}}
				transition={{ duration: 25, repeat: Infinity, ease: [0.4, 0, 0.6, 1] }}
				style={{ willChange: 'transform', transform: 'translateZ(0)' }}
			/>

			{/* Main content */}
			<div className="container relative z-50 text-center px-4">
				<motion.h1 
					className="text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight mb-6 relative"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
				>
					<span className="text-gradient bg-gradient-to-r from-babyblue via-royalpurple to-orangepeel bg-clip-text text-transparent relative z-10">
						Coming Soon
					</span>
					<span className="absolute inset-0 text-gradient bg-gradient-to-r from-babyblue via-royalpurple to-orangepeel bg-clip-text text-transparent blur-xl opacity-40" style={{ willChange: 'opacity' }}>
						Coming Soon
					</span>
				</motion.h1>
				<motion.p 
					className="text-lg md:text-xl lg:text-2xl text-gray-100 max-w-3xl mx-auto mb-10 leading-relaxed font-medium"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.3, duration: 0.8 }}
					style={{
						textShadow: '0 0 20px rgba(0, 153, 255, 0.3)',
						willChange: 'opacity',
					}}
				>
					Transforming automotive services with AI-driven automation, precision diagnostics, and intelligent workflow optimization.
				</motion.p>
				<motion.div
					className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm md:text-base text-gray-300 font-medium"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.6, duration: 0.8 }}
				>
					<div className="flex items-center gap-2">
						<motion.div 
							className="h-3.5 w-3.5 rounded-full bg-babyblue flex-shrink-0"
							style={{ boxShadow: '0 0 10px #0099FF', willChange: 'transform, opacity', transform: 'translateZ(0)' }}
							animate={{
								scale: [1, 1.25, 1],
								opacity: [0.9, 1, 0.9],
							}}
							transition={{
								duration: 2,
								repeat: Infinity,
								ease: [0.4, 0, 0.6, 1],
							}}
						/>
						<span className="tracking-wide whitespace-nowrap text-gray-100 font-medium">2D / 3D Spatial AI</span>
					</div>
					<span className="text-gray-400 hidden sm:inline">•</span>
					<div className="flex items-center gap-2">
						<motion.div 
							className="h-3.5 w-3.5 rounded-full bg-royalpurple flex-shrink-0"
							style={{ boxShadow: '0 0 10px #9900FF', willChange: 'transform, opacity', transform: 'translateZ(0)' }}
							animate={{
								scale: [1, 1.25, 1],
								opacity: [0.9, 1, 0.9],
							}}
							transition={{
								duration: 2,
								repeat: Infinity,
								ease: [0.4, 0, 0.6, 1],
								delay: 0.5,
							}}
						/>
						<span className="tracking-wide whitespace-nowrap text-gray-100 font-medium">Heimdall LLM</span>
					</div>
					<span className="text-gray-400 hidden sm:inline">•</span>
					<div className="flex items-center gap-2">
						<motion.div 
							className="h-3.5 w-3.5 rounded-full bg-orangepeel flex-shrink-0"
							style={{ boxShadow: '0 0 10px #FF9900', willChange: 'transform, opacity', transform: 'translateZ(0)' }}
							animate={{
								scale: [1, 1.25, 1],
								opacity: [0.9, 1, 0.9],
							}}
							transition={{
								duration: 2,
								repeat: Infinity,
								ease: [0.4, 0, 0.6, 1],
								delay: 1,
							}}
						/>
						<span className="tracking-wide whitespace-nowrap text-gray-100 font-medium">Agentic AI</span>
					</div>
				</motion.div>
			</div>

			{/* Neural Network Grid - nodes and connections */}
			<NeuralNetworkGrid />
			
			{/* Optimized corner accent glows */}
			<div className="absolute top-0 left-0 w-96 h-96 bg-babyblue/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2" />
			<div className="absolute bottom-0 right-0 w-96 h-96 bg-royalpurple/10 rounded-full blur-2xl translate-x-1/2 translate-y-1/2" />
		</div>
	);
}
